import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { AlertTriangle } from 'lucide-react';

const UnsavedChangesContext = createContext({
  setUnsavedChanges: () => {},
  requestLeave: (action) => action(),
});

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);

export function UnsavedChangesProvider({ children }) {
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [pending, setPending] = useState(null); // { run: fn }
  const navigate = useNavigate();

  const setUnsavedChanges = useCallback((value) => {
    dirtyRef.current = !!value;
    setDirty(!!value);
  }, []);

  // Used by explicit (programmatic) navigations, e.g. the in-app Back button.
  const requestLeave = useCallback((action) => {
    if (dirtyRef.current) setPending({ run: action });
    else action();
  }, []);

  // Intercept in-app <a> link clicks (sidebar, logo, any internal Link) while dirty.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('#') || href.startsWith('mailto:')
          || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      if (href === window.location.pathname + window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      setPending({ run: () => navigate(href) });
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [dirty, navigate]);

  // Guard the browser / hardware Back button via a history trap.
  useEffect(() => {
    if (!dirty) return undefined;
    window.history.pushState(null, '', window.location.href);
    const onPop = () => {
      // Re-add the trap so we stay on the page, then ask.
      window.history.pushState(null, '', window.location.href);
      setPending({ run: () => { dirtyRef.current = false; setDirty(false); window.history.go(-2); } });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [dirty]);

  const confirmLeave = () => {
    const action = pending?.run;
    dirtyRef.current = false;
    setDirty(false);
    setPending(null);
    if (action) setTimeout(action, 0);
  };
  const cancelLeave = () => setPending(null);

  return (
    <UnsavedChangesContext.Provider value={{ setUnsavedChanges, requestLeave }}>
      {children}
      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) cancelLeave(); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="unsaved-leave-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" /> Unsaved changes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You have unsaved changes on this deal. If you leave now, your edits will be lost.
            </p>
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={cancelLeave} data-testid="unsaved-stay-btn"
                className="text-xs h-9 px-3 border border-border rounded-sm text-foreground hover:bg-secondary">
                Stay on page
              </Button>
              <Button onClick={confirmLeave} data-testid="unsaved-leave-btn"
                className="bg-red-600 hover:bg-red-500 text-white rounded-sm text-xs h-9 px-4">
                Leave without saving
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
}
